import {
  Camera,
  ChevronDown,
  ExternalLink,
  FileSpreadsheet,
  LockKeyhole,
  LogOut,
  Mail,
  Pencil,
  ShieldCheck,
  UserRound,
} from 'lucide-react'

import {
  useEffect,
  useState,
} from 'react'

import { PageHeader } from '../components/ui/PageHeader'
import { CRM_CONFIG } from '../config'
import { useAuth } from '../context/AuthContext'

import {
  connectGmail,
  GMAIL_OAUTH_HOST_MESSAGE,
  loadGmailStatus,
  type GmailOAuthMode,
  type GmailStatus,
} from '../features/gmail/gmailClient'

import {
  loadOwnerProfile,
  saveOwnerProfile,
  uploadOwnerProfilePhoto,
  type OwnerProfile,
} from '../features/profile/profileClient'

import type { CrmDataState } from '../hooks/useCrmData'

const FALLBACK_PROFILE: OwnerProfile = {
  name: 'William Jones',
  email: '',
  title: 'CEO',
  timezone: '',
  updatedAt: null,
}

function formatMoment(
  value: string | null,
  timezone?: string,
): string {
  if (!value) return 'Not available'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return 'Not available'
  }

  try {
    return new Intl.DateTimeFormat(
      'en-US',
      {
        dateStyle: 'medium',
        timeStyle: 'short',
        ...(timezone
          ? { timeZone: timezone }
          : {}),
      },
    ).format(date)
  } catch {
    return new Intl.DateTimeFormat(
      'en-US',
      {
        dateStyle: 'medium',
        timeStyle: 'short',
      },
    ).format(date)
  }
}

export function ProfilePage({
  dataState: _dataState,
}: {
  dataState: CrmDataState
}) {
  const {
    logout,
    session,
  } = useAuth()

  const [profile, setProfile] =
    useState<OwnerProfile>(
      FALLBACK_PROFILE,
    )

  const [draft, setDraft] =
    useState<OwnerProfile>(
      FALLBACK_PROFILE,
    )

  const [editing, setEditing] =
    useState(false)

  const [
    loadingProfile,
    setLoadingProfile,
  ] = useState(true)

  const [
    savingProfile,
    setSavingProfile,
  ] = useState(false)

  const [
    profileError,
    setProfileError,
  ] = useState('')

  const [
    profileMessage,
    setProfileMessage,
  ] = useState('')

  const [
    selectedPhoto,
    setSelectedPhoto,
  ] = useState<File | null>(null)

  const [
    photoPreview,
    setPhotoPreview,
  ] = useState<string | null>(null)

  const [
    photoVersion,
    setPhotoVersion,
  ] = useState(0)

  const [
    photoMissing,
    setPhotoMissing,
  ] = useState(false)

  const [
    gmail,
    setGmail,
  ] = useState<GmailStatus | null>(null)

  const [
    gmailNotice,
    setGmailNotice,
  ] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null)

  function startGmailAuthorization(mode: GmailOAuthMode) {
    if (!connectGmail(mode)) {
      setGmailNotice({ tone: 'info', text: GMAIL_OAUTH_HOST_MESSAGE })
    }
  }

  useEffect(() => {
    let active = true

    loadGmailStatus()
      .then((status) => {
        if (active) setGmail(status)
      })
      .catch(() => {
        if (active) setGmail(null)
      })

    return () => {
      active = false
    }
  }, [])

  /*
   * The OAuth callback returns to this page with a result flag. Only the
   * flag is read - tokens never travel through the URL.
   */
  useEffect(() => {
    const query = new URLSearchParams(
      window.location.search,
    )

    const result = query.get('gmail')

    if (!result) return

    const switching = query.get('mode') === 'switch'
    const reason = query.get('reason')

    setGmailNotice(
      result === 'connected'
        ? {
            tone: 'success',
            text: switching
              ? 'Gmail account switched. New emails send from the account you chose.'
              : 'Gmail account connected.',
          }
        : reason === 'host'
          ? { tone: 'info', text: GMAIL_OAUTH_HOST_MESSAGE }
          : switching
            ? {
                tone: 'error',
                text: `Gmail account was not switched${
                  reason === 'denied' ? '' : ' because Google did not complete the request'
                }. The previous account is still connected.`,
              }
            : {
                tone: 'error',
                text:
                  reason === 'denied'
                    ? 'Gmail access was not authorized.'
                    : 'Gmail could not be connected. Try again.',
              },
    )

    window.history.replaceState({}, '', '/profile')

    loadGmailStatus()
      .then(setGmail)
      .catch(() => setGmail(null))
  }, [])

  useEffect(() => {
    let active = true

    loadOwnerProfile()
      .then((nextProfile) => {
        if (!active) return

        setProfile(nextProfile)
        setDraft(nextProfile)
      })
      .catch((cause) => {
        if (!active) return

        setProfileError(
          cause instanceof Error
            ? cause.message
            : 'Profile could not be loaded.',
        )
      })
      .finally(() => {
        if (active) {
          setLoadingProfile(false)
        }
      })

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    return () => {
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview)
      }
    }
  }, [photoPreview])

  function choosePhoto(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file =
      event.target.files?.[0]

    if (!file) return

    const allowed = [
      'image/jpeg',
      'image/png',
      'image/webp',
    ]

    if (!allowed.includes(file.type)) {
      setProfileError(
        'Use a JPG, PNG, or WebP image.',
      )
      event.target.value = ''
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setProfileError(
        'Profile photo must be smaller than 5 MB.',
      )
      event.target.value = ''
      return
    }

    const preview =
      URL.createObjectURL(file)

    setSelectedPhoto(file)
    setPhotoPreview(preview)
    setPhotoMissing(false)
    setProfileError('')
    setProfileMessage('')
  }

  function clearSelectedPhoto() {
    setSelectedPhoto(null)
    setPhotoPreview(null)
  }

  function beginEditing() {
    setDraft(profile)
    clearSelectedPhoto()
    setProfileError('')
    setProfileMessage('')
    setEditing(true)
  }

  function cancelEditing() {
    setDraft(profile)
    clearSelectedPhoto()
    setProfileError('')
    setEditing(false)
  }

  async function saveProfile() {
    if (savingProfile) return

    if (!draft.name.trim()) {
      setProfileError(
        'Full name is required.',
      )
      return
    }

    if (!draft.email.trim()) {
      setProfileError(
        'Email is required.',
      )
      return
    }

    setSavingProfile(true)
    setProfileError('')
    setProfileMessage('')

    try {
      const saved =
        await saveOwnerProfile({
          name: draft.name,
          email: draft.email,
          title: draft.title,
          timezone: draft.timezone,
        })

      setProfile(saved)
      setDraft(saved)

      if (selectedPhoto) {
        try {
          await uploadOwnerProfilePhoto(
            selectedPhoto,
          )

          setPhotoVersion(Date.now())
          setPhotoMissing(false)
        } catch (cause) {
          setProfileError(
            cause instanceof Error
              ? `Profile details saved, but ${cause.message}`
              : 'Profile details saved, but the photo could not be saved.',
          )

          return
        }
      }

      clearSelectedPhoto()
      setEditing(false)

      setProfileMessage(
        selectedPhoto
          ? 'Profile and photo updated.'
          : 'Profile saved securely.',
      )
    } catch (cause) {
      setProfileError(
        cause instanceof Error
          ? cause.message
          : 'Profile could not be saved.',
      )
    } finally {
      setSavingProfile(false)
    }
  }

  const displayTimezone =
    profile.timezone || undefined

  const initial =
    profile.name
      .trim()
      .charAt(0)
      .toUpperCase() || 'W'

  const photoSource =
    photoPreview ??
    `/api/profile-photo?v=${photoVersion}`

  return (
    <div className="page profile-owner-page">
      <PageHeader
        title="Profile"
        description="Personal account and security."
      />

      <section className="owner-profile-hero">
        {!photoMissing ||
        photoPreview ? (
          <img
            alt={`${profile.name} profile`}
            className="owner-profile-avatar"
            onError={() => {
              if (!photoPreview) {
                setPhotoMissing(true)
              }
            }}
            src={photoSource}
          />
        ) : (
          <div
            aria-hidden="true"
            className="owner-profile-avatar owner-profile-avatar-fallback"
          >
            {initial}
          </div>
        )}

        <div className="owner-profile-identity">
          <h2>{profile.name}</h2>

          <span>
            {profile.title || 'CEO'}
          </span>
        </div>

        <button
          className="button button-secondary owner-profile-edit"
          disabled={loadingProfile}
          onClick={beginEditing}
          type="button"
        >
          <Pencil size={15} />
          Edit Profile
        </button>
      </section>

      {profileMessage ? (
        <div
          className="owner-profile-message is-success"
          role="status"
        >
          <ShieldCheck size={16} />
          {profileMessage}
        </div>
      ) : null}

      {profileError && !editing ? (
        <div
          className="owner-profile-message is-error"
          role="alert"
        >
          {profileError}
        </div>
      ) : null}

      <section className="owner-profile-section">
        <header>
          <UserRound size={19} />

          <div>
            <h2>Account</h2>
            <p>
              William's personal account details.
            </p>
          </div>
        </header>

        {editing ? (
          <div className="owner-profile-form">

            <div className="owner-profile-photo-editor">
              <div className="owner-profile-photo-editor-preview">
                {!photoMissing ||
                photoPreview ? (
                  <img
                    alt="Profile preview"
                    src={photoSource}
                    onError={() => {
                      if (!photoPreview) {
                        setPhotoMissing(true)
                      }
                    }}
                  />
                ) : (
                  <div>
                    {initial}
                  </div>
                )}
              </div>

              <div className="owner-profile-photo-editor-copy">
                <strong>
                  Profile photo
                </strong>

                <span>
                  JPG, PNG, or WebP. Maximum 5 MB.
                </span>

                <label
                  className="button button-secondary owner-profile-photo-button"
                  htmlFor="owner-profile-photo-input"
                >
                  <Camera size={15} />

                  {selectedPhoto
                    ? 'Choose Different Photo'
                    : 'Change Profile Photo'}
                </label>

                <input
                  accept="image/jpeg,image/png,image/webp"
                  className="owner-profile-photo-input"
                  id="owner-profile-photo-input"
                  onChange={choosePhoto}
                  type="file"
                />

                {selectedPhoto ? (
                  <small className="owner-profile-selected-photo">
                    Selected: {selectedPhoto.name}
                  </small>
                ) : null}
              </div>
            </div>

            <label>
              <span>Full name *</span>

              <input
                autoComplete="name"
                maxLength={120}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                value={draft.name}
              />
            </label>

            <label>
              <span>Email *</span>

              <input
                autoComplete="email"
                maxLength={254}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                placeholder="Required"
                type="email"
                value={draft.email}
              />

              <small>
                Required for account identity and future security notifications.
              </small>
            </label>

            <label>
              <span>Title</span>

              <input
                maxLength={80}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
                placeholder="CEO"
                value={draft.title}
              />
            </label>

            <label>
              <span>Timezone</span>

              <input
                maxLength={80}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    timezone:
                      event.target.value,
                  }))
                }
                placeholder="America/New_York"
                value={draft.timezone}
              />
            </label>

            {profileError ? (
              <p
                className="owner-profile-form-error"
                role="alert"
              >
                {profileError}
              </p>
            ) : null}

            <div className="owner-profile-form-actions">
              <button
                className="button button-secondary"
                disabled={savingProfile}
                onClick={cancelEditing}
                type="button"
              >
                Cancel
              </button>

              <button
                className="button button-primary"
                disabled={savingProfile}
                onClick={saveProfile}
                type="button"
              >
                {savingProfile
                  ? 'Saving...'
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <dl className="owner-profile-details">
            <div>
              <dt>Full name</dt>
              <dd>{profile.name}</dd>
            </div>

            <div>
              <dt>Email</dt>

              <dd
                className={
                  profile.email
                    ? ''
                    : 'is-required'
                }
              >
                <Mail size={15} />

                {profile.email ||
                  'Required · Add email'}
              </dd>
            </div>

            <div>
              <dt>Title</dt>
              <dd>
                {profile.title || 'CEO'}
              </dd>
            </div>

            <div>
              <dt>Timezone</dt>
              <dd>
                {profile.timezone ||
                  'Not set'}
              </dd>
            </div>
          </dl>
        )}
      </section>

      <section className="owner-profile-section">
        <header>
          <ShieldCheck size={19} />

          <div>
            <h2>Security</h2>
            <p>
              Account access and sign-in security.
            </p>
          </div>
        </header>

        <dl className="owner-profile-details">
          <div>
            <dt>Passcode</dt>

            <dd>
              <LockKeyhole size={15} />
              Protected
            </dd>
          </div>

          <div>
            <dt>Last sign-in</dt>

            <dd>
              {formatMoment(
                session?.signedInAt ?? null,
                displayTimezone,
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section
        aria-labelledby="owner-profile-gmail-title"
        className="owner-profile-section owner-profile-gmail"
      >
        <header>
          <Mail aria-hidden="true" size={19} />

          <div>
            <h2 id="owner-profile-gmail-title">Gmail</h2>
            <p>
              Send email through the connected Gmail account.
            </p>
          </div>
        </header>

        <dl className="owner-profile-gmail-details">
          <div>
            <dt>Connection</dt>

            <dd>
              {gmail === null ? (
                'Checking...'
              ) : gmail.connected ? (
                <>
                  <span
                    aria-hidden="true"
                    className="owner-profile-gmail-dot"
                  />
                  Connected
                </>
              ) : gmail.enabled ? (
                'Not connected'
              ) : (
                'Not enabled'
              )}
            </dd>
          </div>

          <div>
            <dt>Gmail permission</dt>
            <dd>Send email only</dd>
          </div>
        </dl>

        {gmailNotice || gmail?.enabled ? (
          <div className="owner-profile-gmail-footer">
            {gmailNotice ? (
              <p
                className={`owner-profile-gmail-note is-${gmailNotice.tone}`}
                role="status"
              >
                {gmailNotice.text}
              </p>
            ) : null}

            {gmail?.enabled ? (
              <button
                className={`button ${
                  gmail.connected ? 'button-secondary' : 'button-primary'
                }`}
                onClick={() =>
                  startGmailAuthorization(
                    gmail.connected ? 'switch' : 'connect',
                  )
                }
                type="button"
              >
                {gmail.connected ? 'Switch Gmail account' : 'Connect Gmail'}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      <details className="owner-profile-advanced">
        <summary>
          <div>
            <FileSpreadsheet size={18} />

            <span>
              <strong>Advanced</strong>
              <small>
                Google Sheets source
              </small>
            </span>
          </div>

          <ChevronDown
            aria-hidden="true"
            size={17}
          />
        </summary>

        <div className="owner-profile-advanced-content">
          <dl className="owner-profile-details">
            <div>
              <dt>Workbook</dt>
              <dd>
                {CRM_CONFIG.workbookName}
              </dd>
            </div>

            <div>
              <dt>CRM areas</dt>
              <dd>
                Dashboard, Organizations,
                Opportunities
              </dd>
            </div>

            <div>
              <dt>Protected updates</dt>
              <dd>
                Missing fields only
              </dd>
            </div>

            <div>
              <dt>Workbook timezone</dt>
              <dd>
                {CRM_CONFIG.sheetTimeZone}
              </dd>
            </div>
          </dl>

          <a
            className="button button-secondary owner-profile-sheet-link"
            href={CRM_CONFIG.sheetUrl}
            rel="noreferrer"
            target="_blank"
          >
            Open Google Sheet
            <ExternalLink size={15} />
          </a>
        </div>      </details>

      <div className="owner-profile-standalone-signout">
        <button
          className="owner-profile-signout-button"
          onClick={logout}
          type="button"
        >
          <LogOut size={15} />
          Sign Out
        </button>
      </div>
    </div>
  )
}



